import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { getLscAutoSyncRuns, getLscSyncStatus, getScheduleRepairStatus, previewScheduleRepair, applyScheduleRepair, syncLscScheduleSource } from '../api';
import type { LscAutoSyncRun, LscScheduleSourceSyncResult, LscSyncStatus, ScheduleRepairApplyResponse, ScheduleRepairPreviewResponse, ScheduleRepairStatus } from '../types';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';
import LscMutationErrorAlert from './LscMutationErrorAlert';
import { ReportIssueTrigger } from './support/ReportIssueTrigger';
import { lscPath } from '../routing';
import { displaySeasonLabel } from '../seasonDisplay';
import {
  SCHEDULE_SYNC_ROLLOVER_DETECTED_COPY,
  SCHEDULE_SYNC_ROLLOVER_REVIEW_COPY,
  SCHEDULE_SYNC_TEAM_ASSIGNMENT_REVIEW_COPY,
  SCHEDULE_SOURCE_STALE_REVIEW_COPY,
  PROVIDER_LINK_BROKEN_COPY,
  PROVIDER_LINK_WAITING_FOR_M27_COPY,
  PROVIDER_LINK_HELD_FOR_REVIEW_COPY,
  guardedAutoSyncApplyCustomerCopy,
} from '../setupScheduleCopy';
import {
  formatScheduleRepairDiffSummary,
  parseScheduleRepairError,
  SCHEDULE_REPAIR_APPLY_CTA,
  SCHEDULE_REPAIR_APPLY_WARNING,
  SCHEDULE_REPAIR_CONFLICTS_COPY,
  SCHEDULE_REPAIR_NO_CHANGES_COPY,
  SCHEDULE_REPAIR_NOT_CONFIGURED_COPY,
  SCHEDULE_REPAIR_PREVIEW_CTA,
  SCHEDULE_REPAIR_PREVIEW_READY_TITLE,
  SCHEDULE_REPAIR_REVIEW_REQUIRED_COPY,
  SCHEDULE_REPAIR_SOURCE_UNAVAILABLE_COPY,
  SCHEDULE_REPAIR_SUCCESS_COPY,
  SCHEDULE_REPAIR_SUCCESS_TITLE,
  scheduleRepairStatusDetail,
  scheduleRepairStatusHeadline,
} from '../scheduleRepairCopy';

export type ScheduleSyncStatusPanelProps = {
  leagueId: string;
  compact?: boolean;
  onRefreshComplete?: (status: LscSyncStatus) => void;
  onApplySuccess?: () => void;
  /** Repair and run history only. The import page owns the status line and the one action. */
  detailsOnly?: boolean;
};

export const SCHEDULE_SYNC_APPLY_WARNING =
  "This will update LSC's active schedule week to match NeonSportz.";
export const SCHEDULE_SYNC_SCHEDULE_DATA_REVIEW_COPY =
  "This updates LSC's active week. Schedule matchup changes may still require review.";
export const SCHEDULE_SYNC_REVIEW_REQUIRED_COPY =
  'Review required before LSC can advance.';
export const SCHEDULE_SYNC_ALREADY_IN_SYNC_COPY = 'Schedule is already in sync';
export const SCHEDULE_SYNC_APPLY_SUCCESS_TITLE = 'LSC schedule advanced';

export function scheduleSyncApplySuccessDetail(
  weekLabel: string,
  remainingScheduleHolds?: string[] | null
): string {
  const base = `LSC now uses ${weekLabel} for live detection and archive matching.`;
  if (remainingScheduleHolds && remainingScheduleHolds.length > 0) {
    return `${base} Schedule matchup changes may still require review.`;
  }
  return base;
}

export function hasStageWeekDrift(
  status: LscSyncStatus | null,
  detectedWeek?: string | null
): boolean {
  const active = status?.active_stage_label;
  const detected =
    detectedWeek ??
    status?.detected_stage_label ??
    status?.provider_stage_label ??
    status?.schedule_source?.last_detected_week;
  if (!active || !detected) {
    return Boolean(status?.is_stale);
  }
  return detected.trim().toLowerCase() !== active.trim().toLowerCase();
}

function stagePointerBlockingHolds(preview: LscScheduleSourceSyncResult | null): string[] {
  if (!preview) return [];
  if (preview.stage_pointer_apply_holds && preview.stage_pointer_apply_holds.length > 0) {
    return preview.stage_pointer_apply_holds;
  }
  const scheduleDataHolds = new Set(preview.schedule_data_apply_holds ?? []);
  return (preview.hold_reasons ?? []).filter((hold) => !scheduleDataHolds.has(hold));
}

function scheduleDataReviewHolds(preview: LscScheduleSourceSyncResult | null): string[] {
  if (!preview) return [];
  if (preview.schedule_data_apply_holds && preview.schedule_data_apply_holds.length > 0) {
    return preview.schedule_data_apply_holds;
  }
  return preview.remaining_schedule_holds ?? [];
}

function formatSyncTimestamp(value?: string | null): string {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatHoldReason(hold: string): string {
  return hold.replace(/_/g, ' ');
}

export function hasSeasonRolloverReview(
  status: LscSyncStatus | null,
  preview?: LscScheduleSourceSyncResult | null
): boolean {
  return Boolean(
    preview?.rollover_review_required ||
      status?.rollover_review_required ||
      preview?.season_changed ||
      status?.season_changed
  );
}

export function ScheduleSyncStatusPanelView({
  leagueId,
  status,
  runs,
  loading,
  error,
  syncing,
  applying,
  syncPreview,
  syncApplyResult,
  syncError,
  onRefresh,
  onSync,
  onApply,
  repairStatus,
  repairStatusLoading,
  repairStatusError,
  previewingRepair,
  applyingRepair,
  repairPreview,
  repairApplyResult,
  repairActionError,
  onPreviewRepair,
  onApplyRepair,
  detailsOnly = false,
}: {
  leagueId: string;
  status: LscSyncStatus | null;
  runs: LscAutoSyncRun[];
  loading: boolean;
  error: string | null;
  syncing: boolean;
  applying: boolean;
  syncPreview: LscScheduleSourceSyncResult | null;
  syncApplyResult: LscScheduleSourceSyncResult | null;
  syncError: ParsedLscMutationError | null;
  onRefresh: () => void;
  onSync: () => void;
  onApply: () => void;
  repairStatus: ScheduleRepairStatus | null;
  repairStatusLoading: boolean;
  repairStatusError: string | null;
  previewingRepair: boolean;
  applyingRepair: boolean;
  repairPreview: ScheduleRepairPreviewResponse | null;
  repairApplyResult: ScheduleRepairApplyResponse | null;
  repairActionError: ParsedLscMutationError | null;
  onPreviewRepair: () => void;
  onApplyRepair: () => void;
  detailsOnly?: boolean;
}) {
  const autoSync = status?.auto_sync;
  const scheduleSource = status?.schedule_source;
  const sourceConnected = Boolean(scheduleSource?.enabled);
  const detectedWeek =
    syncPreview?.detected_stage_label ||
    status?.detected_stage_label ||
    status?.provider_stage_label ||
    scheduleSource?.last_detected_week;
  const showStageStaleBanner = hasStageWeekDrift(status, detectedWeek) && !hasSeasonRolloverReview(status, syncPreview);
  const showSeasonRolloverBanner = Boolean(
    sourceConnected &&
      hasSeasonRolloverReview(status, syncPreview) &&
      !loading &&
      !error
  );
  const showUnifiedRepairSection = Boolean(
    repairStatus?.repair_needed && !loading && !error && !showSeasonRolloverBanner
  );
  const showRepairNotConfigured = Boolean(
    showUnifiedRepairSection && repairStatus?.recommended_action === 'connect_source'
  );
  const showRepairConflicts = Boolean(
    showUnifiedRepairSection &&
      (repairStatus?.recommended_action === 'review_conflicts' ||
        repairPreview?.requires_review ||
        (repairPreview?.diff_summary?.conflicts ?? 0) > 0)
  );
  const repairPreviewReady = Boolean(repairPreview && !repairApplyResult?.applied);
  const showRepairApplyCta = Boolean(
    repairPreviewReady && repairPreview?.safe_to_apply && !repairPreview?.requires_review
  );
  const repairDiffLines = repairPreview
    ? formatScheduleRepairDiffSummary(repairPreview.diff_summary)
    : [];
  const previewReady = syncPreview && !syncPreview.applied;
  const pointerHolds = stagePointerBlockingHolds(syncPreview);
  const scheduleDataHolds = scheduleDataReviewHolds(syncPreview);
  const remainingAfterApply = syncApplyResult?.remaining_schedule_holds ?? [];
  const alreadyInSync = Boolean(
    previewReady &&
      !syncPreview?.out_of_sync &&
      (pointerHolds.length === 0 ||
        (pointerHolds.length === 1 && pointerHolds[0] === 'schedule_already_in_sync'))
  );
  const showApplyCta = Boolean(
    previewReady &&
      syncPreview?.safe_to_apply &&
      syncPreview?.out_of_sync &&
      detectedWeek &&
      !syncPreview?.rollover_review_required
  );
  const showScheduleDataReviewNote = Boolean(
    showApplyCta &&
      (scheduleDataHolds.length > 0 ||
        (syncPreview?.warnings ?? []).some((warning) =>
          warning.toLowerCase().includes('schedule matchup')
        ))
  );
  const showPersistedScheduleDataReview = Boolean(
    sourceConnected &&
      !showStageStaleBanner &&
      !previewReady &&
      !syncApplyResult?.applied &&
      scheduleSource?.sync_status === 'needs_attention'
  );
  const showAlreadyInSync = alreadyInSync;
  const showRolloverBlockedPreview = Boolean(previewReady && syncPreview?.rollover_review_required);
  const showTeamAssignmentBlockedPreview = Boolean(
    previewReady && syncPreview?.team_assignment_review_required
  );
  const showBlockedPreview = Boolean(
    previewReady &&
      !showApplyCta &&
      !showAlreadyInSync &&
      (pointerHolds.length > 0 || syncPreview?.out_of_sync)
  );

  return (
    <div className="card space-y-4" data-testid="schedule-sync-status-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Schedule sync status</h2>
          <p className="text-sm text-secondary-400 mt-1">
            Commissioner-controlled refresh state for Live League Games and VOD matching.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="btn-secondary inline-flex items-center gap-2 text-sm shrink-0"
          aria-label="Refresh sync status"
          data-testid="schedule-sync-status-refresh"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-400" data-testid="schedule-sync-status-loading">
          Loading sync status...
        </p>
      ) : error ? (
        <p className="text-sm text-red-300" data-testid="schedule-sync-status-error">
          {error}
        </p>
      ) : status ? (
        <>
          {!sourceConnected ? (
            <div
              className="rounded-lg border border-secondary-700 bg-secondary-900/40 px-4 py-3 text-sm text-secondary-200"
              data-testid="schedule-sync-no-source"
            >
              No NeonSportz schedule source connected.{' '}
              <Link
                to={lscPath(`/leagues/${leagueId}/settings/schedule-source`)}
                className="text-primary-300 hover:text-primary-200 underline"
              >
                Connect in League Settings → Schedule Source
              </Link>
              .
            </div>
          ) : null}

          {status?.provider_link_broken ? (
            <div
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex gap-3"
              data-testid="schedule-provider-link-broken"
            >
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" aria-hidden />
              <div className="text-sm text-amber-100 space-y-2">
                <p className="font-medium">NeonSportz link needs attention</p>
                <p data-testid="schedule-provider-link-message">
                  {status.provider_link_message || PROVIDER_LINK_BROKEN_COPY}
                </p>
                {status.provider_link_suggestion?.provider_slug ? (
                  <div
                    className="rounded border border-amber-500/30 bg-black/20 px-3 py-2 space-y-1"
                    data-testid="schedule-provider-link-suggestion"
                  >
                    <p>
                      We found{' '}
                      {status.provider_link_suggestion.name ||
                        status.provider_link_suggestion.abbrev}{' '}
                      ({status.provider_link_suggestion.provider_slug})
                      {status.provider_link_suggestion.madden_year
                        ? ` on Madden ${String(status.provider_link_suggestion.madden_year).slice(-2)}`
                        : ''}
                      . Link it?
                    </p>
                    <Link
                      to={lscPath(`/leagues/${leagueId}/import`)}
                      className="btn-secondary inline-block text-sm"
                      data-testid="schedule-provider-link-suggestion-cta"
                    >
                      Review and link {status.provider_link_suggestion.provider_slug}
                    </Link>
                  </div>
                ) : (
                  <p data-testid="schedule-provider-link-paste-hint">
                    {PROVIDER_LINK_WAITING_FOR_M27_COPY}
                  </p>
                )}
                <Link
                  to={lscPath(`/leagues/${leagueId}/import`)}
                  className="btn-secondary inline-block text-sm"
                  data-testid="schedule-provider-link-import-cta"
                >
                  Paste your new NeonSportz link
                </Link>
              </div>
            </div>
          ) : null}

          {!detailsOnly && status?.held_for_review ? (
            <div
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex gap-3"
              data-testid="schedule-held-for-review"
            >
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" aria-hidden />
              <div className="text-sm text-amber-100 space-y-2">
                <p className="font-medium">
                  {autoSync?.auto_sync_last_hold_message || PROVIDER_LINK_HELD_FOR_REVIEW_COPY}
                </p>
                <Link
                  to={lscPath(`/leagues/${leagueId}/import`)}
                  className="btn-secondary inline-block text-sm"
                >
                  Review pending schedule changes
                </Link>
              </div>
            </div>
          ) : null}

          {!detailsOnly && showSeasonRolloverBanner ? (
            <div
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex gap-3"
              data-testid="schedule-sync-rollover-banner"
            >
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" aria-hidden />
              <div className="text-sm text-amber-100 space-y-2">
                <p className="font-medium">New season schedule detected</p>
                <p data-testid="schedule-sync-rollover-copy">{SCHEDULE_SYNC_ROLLOVER_DETECTED_COPY}</p>
                <p data-testid="schedule-sync-rollover-review-copy">{SCHEDULE_SYNC_ROLLOVER_REVIEW_COPY}</p>
                {(syncPreview?.detected_season_label || status?.detected_season_label) &&
                (syncPreview?.active_season_label || status?.active_season_label) ? (
                  <p data-testid="schedule-sync-rollover-season-detail">
                    NeonSportz shows{' '}
                    {displaySeasonLabel(
                      syncPreview?.detected_season_label || status?.detected_season_label
                    )}
                    . LSC is currently on{' '}
                    {displaySeasonLabel(syncPreview?.active_season_label || status?.active_season_label)}
                    .
                  </p>
                ) : null}
                <Link
                  to={lscPath(`/leagues/${leagueId}/import`)}
                  className="btn-secondary inline-block text-sm"
                  data-testid="schedule-sync-rollover-import-cta"
                >
                  Review import and apply
                </Link>
              </div>
            </div>
          ) : null}

          {!detailsOnly && showStageStaleBanner ? (
            <div
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex gap-3"
              data-testid="schedule-sync-stale-warning"
            >
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" aria-hidden />
              <div className="text-sm text-amber-100 space-y-2">
                <p className="font-medium">Schedule may be stale</p>
                <p data-testid="schedule-sync-stale-copy">
                  {detectedWeek && status.active_stage_label
                    ? `NeonSportz shows ${detectedWeek}. LSC is currently on ${status.active_stage_label}.`
                    : SCHEDULE_SOURCE_STALE_REVIEW_COPY}
                </p>
                {sourceConnected ? (
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={onSync}
                    disabled={syncing || applying || previewingRepair || applyingRepair}
                    data-testid="schedule-sync-from-neonsportz"
                  >
                    {syncing ? 'Syncing…' : 'Sync from NeonSportz'}
                  </button>
                ) : null}
              </div>
            </div>
          ) : !detailsOnly && sourceConnected ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm text-secondary-300">Schedule source connected.</p>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={onSync}
                  disabled={syncing || applying}
                  data-testid="schedule-sync-from-neonsportz"
                >
                  {syncing ? 'Syncing…' : 'Sync from NeonSportz'}
                </button>
              </div>
              {showPersistedScheduleDataReview ? (
                <p
                  className="text-sm text-secondary-300"
                  data-testid="schedule-sync-schedule-data-review-note"
                >
                  {SCHEDULE_SYNC_SCHEDULE_DATA_REVIEW_COPY}
                </p>
              ) : null}
            </div>
          ) : null}

          {syncError ? (
            <div data-testid="schedule-sync-action-error">
              <LscMutationErrorAlert error={syncError} />
              <div className="mt-2">
                <ReportIssueTrigger
                  variant="inline"
                  testId="schedule-sync-report-issue"
                  prefill={{
                    leagueId,
                    surface: 'schedule_sync_status_panel',
                    category: 'schedule',
                    title: 'Schedule sync issue',
                  }}
                />
              </div>
            </div>
          ) : null}

          {repairStatusLoading ? (
            <p className="text-sm text-secondary-400" data-testid="schedule-repair-status-loading">
              Loading schedule repair status...
            </p>
          ) : repairStatusError ? (
            <p className="text-sm text-red-300" data-testid="schedule-repair-status-error">
              {repairStatusError}
            </p>
          ) : showUnifiedRepairSection ? (
            <div
              id="schedule-repair-section"
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex gap-3"
              data-testid="schedule-repair-section"
            >
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" aria-hidden />
              <div className="text-sm text-amber-100 space-y-2">
                <p className="font-medium" data-testid="schedule-repair-headline">
                  {scheduleRepairStatusHeadline(repairStatus)}
                </p>
                <p data-testid="schedule-repair-detail">{scheduleRepairStatusDetail(repairStatus)}</p>
                {showRepairNotConfigured ? (
                  <p data-testid="schedule-repair-not-configured-copy">
                    {SCHEDULE_REPAIR_NOT_CONFIGURED_COPY}{' '}
                    <Link
                      to={lscPath(`/leagues/${leagueId}/settings/schedule-source`)}
                      className="text-primary-300 hover:text-primary-200 underline"
                    >
                      Connect schedule source
                    </Link>
                    .
                  </p>
                ) : repairStatus?.can_preview ? (
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={onPreviewRepair}
                    disabled={previewingRepair || applyingRepair || syncing || applying}
                    data-testid="schedule-repair-preview-cta"
                  >
                    {previewingRepair ? 'Previewing…' : SCHEDULE_REPAIR_PREVIEW_CTA}
                  </button>
                ) : null}
                {showRepairConflicts ? (
                  <p className="text-amber-100/90" data-testid="schedule-repair-conflicts-copy">
                    {SCHEDULE_REPAIR_CONFLICTS_COPY}{' '}
                    <Link
                      to={lscPath(`/leagues/${leagueId}/members`)}
                      className="text-primary-300 hover:text-primary-200 underline"
                    >
                      Review team assignments
                    </Link>
                  </p>
                ) : null}
              </div>
            </div>
          ) : repairStatus && !repairStatus.repair_needed ? (
            <p className="text-sm text-secondary-400" data-testid="schedule-repair-no-action">
              No schedule repair needed.
            </p>
          ) : null}

          {repairActionError ? (
            <div data-testid="schedule-repair-action-error">
              <LscMutationErrorAlert error={repairActionError} />
              <div className="mt-2">
                <ReportIssueTrigger
                  variant="inline"
                  testId="schedule-repair-report-issue"
                  prefill={{
                    leagueId,
                    surface: 'schedule_repair_status_panel',
                    category: 'schedule',
                    title: 'Schedule repair issue',
                  }}
                />
              </div>
            </div>
          ) : null}

          {repairApplyResult?.applied ? (
            <div
              className="rounded-lg border border-emerald-700/60 bg-emerald-900/20 p-4 space-y-2 text-sm"
              role="status"
              data-testid="schedule-repair-apply-success"
            >
              <p className="font-medium text-emerald-100">{SCHEDULE_REPAIR_SUCCESS_TITLE}</p>
              <p className="text-emerald-200/90">{SCHEDULE_REPAIR_SUCCESS_COPY}</p>
              {repairApplyResult.active_stage_label ? (
                <p className="text-emerald-200/90" data-testid="schedule-repair-apply-context">
                  LSC now uses {repairApplyResult.active_stage_label}
                  {repairApplyResult.active_season_label
                    ? ` (${displaySeasonLabel(repairApplyResult.active_season_label)})`
                    : ''}{' '}
                  for schedule context.
                </p>
              ) : null}
            </div>
          ) : null}

          {repairPreviewReady ? (
            <div
              className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-4 space-y-3 text-sm"
              data-testid="schedule-repair-preview"
            >
              <p className="font-medium text-white">{SCHEDULE_REPAIR_PREVIEW_READY_TITLE}</p>
              {repairPreview?.current_context?.stage_label ||
              repairPreview?.proposed_context?.stage_label ? (
                <p className="text-secondary-300" data-testid="schedule-repair-context-detail">
                  {repairPreview?.current_context?.stage_label
                    ? `Current: ${repairPreview.current_context.stage_label}`
                    : null}
                  {repairPreview?.current_context?.stage_label &&
                  repairPreview?.proposed_context?.stage_label
                    ? ' · '
                    : null}
                  {repairPreview?.proposed_context?.stage_label
                    ? `Proposed: ${repairPreview.proposed_context.stage_label}`
                    : null}
                </p>
              ) : null}
              {repairDiffLines.length > 0 ? (
                <ul
                  className="list-disc list-inside text-secondary-200"
                  data-testid="schedule-repair-diff-summary"
                >
                  {repairDiffLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-secondary-300" data-testid="schedule-repair-no-changes-copy">
                  {SCHEDULE_REPAIR_NO_CHANGES_COPY}
                </p>
              )}
              {showRepairConflicts || repairPreview?.requires_review ? (
                <p className="text-amber-200" data-testid="schedule-repair-review-required-copy">
                  {SCHEDULE_REPAIR_REVIEW_REQUIRED_COPY}
                </p>
              ) : null}
              {showRepairApplyCta ? (
                <div className="space-y-2" data-testid="schedule-repair-apply-cta">
                  <p className="text-amber-100">{SCHEDULE_REPAIR_APPLY_WARNING}</p>
                  <button
                    type="button"
                    className="btn-primary text-sm"
                    onClick={onApplyRepair}
                    disabled={applyingRepair}
                    data-testid="schedule-repair-apply-button"
                  >
                    {applyingRepair ? 'Applying…' : SCHEDULE_REPAIR_APPLY_CTA}
                  </button>
                </div>
              ) : repairPreviewReady && !showRepairApplyCta ? (
                <p className="text-amber-200" data-testid="schedule-repair-apply-blocked">
                  {repairPreview?.requires_review
                    ? SCHEDULE_REPAIR_REVIEW_REQUIRED_COPY
                    : SCHEDULE_REPAIR_SOURCE_UNAVAILABLE_COPY}
                </p>
              ) : null}
            </div>
          ) : null}

          {syncApplyResult?.applied ? (
            <div
              className="rounded-lg border border-emerald-700/60 bg-emerald-900/20 p-4 space-y-2 text-sm"
              role="status"
              data-testid="schedule-sync-apply-success"
            >
              <p className="font-medium text-emerald-100">{SCHEDULE_SYNC_APPLY_SUCCESS_TITLE}</p>
              <p className="text-emerald-200/90">
                {scheduleSyncApplySuccessDetail(
                  syncApplyResult.new_stage_label ||
                    syncApplyResult.active_stage_label ||
                    'the new week',
                  remainingAfterApply
                )}
              </p>
              {remainingAfterApply.length > 0 ? (
                <p
                  className="text-amber-100/90"
                  data-testid="schedule-sync-schedule-data-review-note"
                >
                  {SCHEDULE_SYNC_SCHEDULE_DATA_REVIEW_COPY}
                </p>
              ) : null}
            </div>
          ) : null}

          {previewReady ? (
            <div
              className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-4 space-y-3 text-sm"
              data-testid="schedule-sync-preview"
            >
              <p className="font-medium text-white">Sync preview</p>
              <p className="text-secondary-300">{syncPreview.summary_copy}</p>

              {showAlreadyInSync ? (
                <p className="text-emerald-200" data-testid="schedule-sync-already-in-sync">
                  {SCHEDULE_SYNC_ALREADY_IN_SYNC_COPY}
                </p>
              ) : null}

              {showBlockedPreview ? (
                <div className="space-y-2" data-testid="schedule-sync-apply-blocked">
                  {showRolloverBlockedPreview ? (
                    <>
                      <p className="text-amber-200" data-testid="schedule-sync-preview-rollover-copy">
                        {SCHEDULE_SYNC_ROLLOVER_DETECTED_COPY}
                      </p>
                      <Link
                        to={lscPath(`/leagues/${leagueId}/import`)}
                        className="btn-secondary inline-block text-sm"
                        data-testid="schedule-sync-preview-rollover-import-cta"
                      >
                        Review import and apply
                      </Link>
                    </>
                  ) : showTeamAssignmentBlockedPreview ? (
                    <p className="text-amber-200" data-testid="schedule-sync-team-assignment-review-copy">
                      {SCHEDULE_SYNC_TEAM_ASSIGNMENT_REVIEW_COPY}{' '}
                      <Link
                        to={lscPath(`/leagues/${leagueId}/members`)}
                        className="text-primary-300 hover:text-primary-200 underline"
                      >
                        Review team assignments
                      </Link>
                    </p>
                  ) : (
                    <>
                      <p className="text-amber-200">{SCHEDULE_SYNC_REVIEW_REQUIRED_COPY}</p>
                      {pointerHolds.length > 0 ? (
                        <ul className="list-disc list-inside text-amber-200/90">
                          {pointerHolds.map((hold) => (
                            <li key={hold}>{formatHoldReason(hold)}</li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}

              {!detailsOnly && showApplyCta ? (
                <div className="space-y-2" data-testid="schedule-sync-apply-cta">
                  <p className="text-amber-100">{SCHEDULE_SYNC_APPLY_WARNING}</p>
                  {showScheduleDataReviewNote ? (
                    <p
                      className="text-secondary-300"
                      data-testid="schedule-sync-schedule-data-review-note"
                    >
                      {SCHEDULE_SYNC_SCHEDULE_DATA_REVIEW_COPY}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className="btn-primary text-sm"
                    onClick={onApply}
                    disabled={applying}
                    data-testid="schedule-sync-apply-week"
                  >
                    {applying ? 'Applying…' : `Apply ${detectedWeek}`}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          <dl className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-secondary-500">Last sync</dt>
              <dd className="text-secondary-100" data-testid="schedule-sync-last-sync">
                {formatSyncTimestamp(status.last_sync_at || scheduleSource?.last_sync_at)}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">Source</dt>
              <dd className="text-secondary-100">{status.last_sync_source || scheduleSource?.provider || '—'}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Active season</dt>
              <dd className="text-secondary-100">
                {displaySeasonLabel(status.active_season_label) || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">LSC current week/stage</dt>
              <dd className="text-secondary-100" data-testid="schedule-sync-lsc-current-week">
                {status.active_stage_label || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">NeonSportz current week/stage</dt>
              <dd className="text-secondary-100" data-testid="schedule-sync-provider-current-week">
                {detectedWeek || '—'}
              </dd>
            </div>
            {status.season_phase ? (
              <div>
                <dt className="text-secondary-500">Season phase</dt>
                <dd className="text-secondary-100" data-testid="schedule-sync-season-phase">
                  {status.season_phase}
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="text-secondary-500">Open matches (current stage)</dt>
              <dd className="text-secondary-100">{status.current_open_matches_count}</dd>
            </div>
            {sourceConnected ? (
              <div>
                <dt className="text-secondary-500">Schedule source status</dt>
                <dd className="text-secondary-100">{scheduleSource?.sync_status || 'ready'}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-secondary-500">Members missing Twitch</dt>
              <dd className="text-secondary-100">{status.missing_twitch_count}</dd>
            </div>
            {(status.imported_unclaimed_count ?? 0) > 0 ? (
              <div data-testid="schedule-sync-unclaimed-members">
                <dt className="text-secondary-500">Members who haven't joined</dt>
                <dd className="text-secondary-100">
                  {status.imported_unclaimed_count} members haven&apos;t joined yet —{' '}
                  <Link
                    to={lscPath(`/leagues/${leagueId}/members`)}
                    className="text-primary-300 hover:text-primary-200 underline"
                  >
                    send invites
                  </Link>
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="text-secondary-500">Team assignment mismatches</dt>
              <dd className="text-secondary-100">{status.member_side_mismatch_count}</dd>
            </div>
          </dl>

          {status.guarded_auto_sync_apply ? (
            <div
              className="rounded-lg border border-secondary-700/60 bg-secondary-900/40 p-4 space-y-1 text-sm"
              data-testid="schedule-sync-guarded-auto-sync-apply"
            >
              <p className="font-medium text-secondary-100">Guarded auto-sync apply</p>
              <p className="text-secondary-300" data-testid="schedule-sync-guarded-auto-sync-message">
                {status.guarded_auto_sync_apply.customer_message ||
                  guardedAutoSyncApplyCustomerCopy(status.guarded_auto_sync_apply.mode)}
              </p>
            </div>
          ) : null}

          {autoSync ? (
            <div className="space-y-3" data-testid="schedule-sync-auto-sync-summary">
              <h3 className="text-sm font-medium text-secondary-200">Auto-sync observability</h3>
              {autoSync.auto_sync_last_apply_summary ? (
                <p
                  className="text-sm text-secondary-200 rounded-lg border border-secondary-700/60 bg-secondary-900/40 p-3"
                  data-testid="schedule-sync-auto-apply-history"
                >
                  {autoSync.auto_sync_last_apply_summary}
                </p>
              ) : null}
              <dl className="grid gap-3 sm:grid-cols-2 text-sm">
                <div>
                  <dt className="text-secondary-500">Auto-sync last checked</dt>
                  <dd className="text-secondary-100">
                    {formatSyncTimestamp(autoSync.auto_sync_last_checked_at)}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Auto-sync last status</dt>
                  <dd className="text-secondary-100" data-testid="schedule-sync-auto-sync-status">
                    {autoSync.auto_sync_last_status || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Auto-sync last error</dt>
                  <dd className="text-secondary-100" data-testid="schedule-sync-auto-sync-error">
                    {autoSync.auto_sync_last_error || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Source state stale</dt>
                  <dd className="text-secondary-100">
                    {autoSync.source_state_stale || status.source_state_stale ? 'Yes' : 'No'}
                  </dd>
                </div>
                {autoSync.source_state_stale_reason || status.source_state_stale_reason ? (
                  <div>
                    <dt className="text-secondary-500">Stale reason</dt>
                    <dd className="text-secondary-100">
                      {autoSync.source_state_stale_reason || status.source_state_stale_reason}
                    </dd>
                  </div>
                ) : null}
                {autoSync.auto_sync_lock_until ? (
                  <div>
                    <dt className="text-secondary-500">Auto-sync lock until</dt>
                    <dd className="text-secondary-100">
                      {formatSyncTimestamp(autoSync.auto_sync_lock_until)}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}

          {runs.length > 0 ? (
            <div className="space-y-2" data-testid="schedule-sync-recent-runs">
              <h3 className="text-sm font-medium text-secondary-200">Recent auto-sync runs</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="text-secondary-500">
                    <tr>
                      <th className="pr-3 py-1">Time</th>
                      <th className="pr-3 py-1">Status</th>
                      <th className="pr-3 py-1">Mode</th>
                      <th className="pr-3 py-1">Dry run</th>
                      <th className="pr-3 py-1">Error</th>
                      <th className="pr-3 py-1">Holds</th>
                    </tr>
                  </thead>
                  <tbody className="text-secondary-100">
                    {runs.map((run) => (
                      <tr key={run.id || `${run.started_at}-${run.status}`}>
                        <td className="pr-3 py-1">{formatSyncTimestamp(run.started_at)}</td>
                        <td className="pr-3 py-1">{run.status || '—'}</td>
                        <td className="pr-3 py-1">{run.operation_mode || '—'}</td>
                        <td className="pr-3 py-1">{run.dry_run ? 'Yes' : 'No'}</td>
                        <td className="pr-3 py-1">{run.error_code || '—'}</td>
                        <td className="pr-3 py-1">{run.hold_reasons?.length ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {status.warnings.length > 0 ? (
            <ul
              className="list-disc list-inside text-sm text-secondary-400 space-y-1"
              data-testid="schedule-sync-warnings"
            >
              {status.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const ScheduleSyncStatusPanel: React.FC<ScheduleSyncStatusPanelProps> = ({
  leagueId,
  compact,
  onRefreshComplete,
  onApplySuccess,
  detailsOnly = false,
}) => {
  const [status, setStatus] = useState<LscSyncStatus | null>(null);
  const [runs, setRuns] = useState<LscAutoSyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [syncPreview, setSyncPreview] = useState<LscScheduleSourceSyncResult | null>(null);
  const [syncApplyResult, setSyncApplyResult] = useState<LscScheduleSourceSyncResult | null>(null);
  const [syncError, setSyncError] = useState<ParsedLscMutationError | null>(null);
  const [repairStatus, setRepairStatus] = useState<ScheduleRepairStatus | null>(null);
  const [repairStatusLoading, setRepairStatusLoading] = useState(true);
  const [repairStatusError, setRepairStatusError] = useState<string | null>(null);
  const [previewingRepair, setPreviewingRepair] = useState(false);
  const [applyingRepair, setApplyingRepair] = useState(false);
  const [repairPreview, setRepairPreview] = useState<ScheduleRepairPreviewResponse | null>(null);
  const [repairApplyResult, setRepairApplyResult] = useState<ScheduleRepairApplyResponse | null>(null);
  const [repairActionError, setRepairActionError] = useState<ParsedLscMutationError | null>(null);

  const loadRepairStatus = useCallback(async () => {
    setRepairStatusLoading(true);
    setRepairStatusError(null);
    try {
      const next = await getScheduleRepairStatus(leagueId);
      setRepairStatus(next);
    } catch {
      setRepairStatus(null);
      setRepairStatusError('Schedule repair status unavailable.');
    } finally {
      setRepairStatusLoading(false);
    }
  }, [leagueId]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await getLscSyncStatus(leagueId);
      setStatus(next);
      onRefreshComplete?.(next);
      try {
        const runData = await getLscAutoSyncRuns(leagueId, 5);
        setRuns(runData.runs);
      } catch {
        setRuns([]);
      }
      await loadRepairStatus();
    } catch {
      setError('Schedule sync status unavailable.');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [leagueId, loadRepairStatus, onRefreshComplete]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncError(null);
    setSyncPreview(null);
    setSyncApplyResult(null);
    try {
      const preview = await syncLscScheduleSource(leagueId);
      setSyncPreview(preview);
      await loadStatus();
    } catch (err) {
      setSyncError(parseLscMutationError(err, 'Schedule sync preview failed.'));
    } finally {
      setSyncing(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setSyncError(null);
    setSyncApplyResult(null);
    try {
      const result = await syncLscScheduleSource(leagueId, { apply: true, dry_run: false });
      setSyncApplyResult(result);
      setSyncPreview(null);
      await loadStatus();
      onApplySuccess?.();
    } catch (err) {
      setSyncError(parseLscMutationError(err, 'Schedule sync apply failed.'));
    } finally {
      setApplying(false);
    }
  };

  const handlePreviewRepair = async () => {
    setPreviewingRepair(true);
    setRepairActionError(null);
    setRepairPreview(null);
    setRepairApplyResult(null);
    try {
      const preview = await previewScheduleRepair(leagueId);
      setRepairPreview(preview);
    } catch (err) {
      setRepairActionError(parseScheduleRepairError(err, 'Schedule repair preview failed.'));
    } finally {
      setPreviewingRepair(false);
    }
  };

  const handleApplyRepair = async () => {
    if (!repairPreview?.repair_run_id) {
      setRepairActionError({
        message: 'Run a schedule repair preview before applying.',
        showBillingLink: false,
        showPricingLink: false,
      });
      return;
    }
    setApplyingRepair(true);
    setRepairActionError(null);
    setRepairApplyResult(null);
    try {
      const result = await applyScheduleRepair(leagueId, {
        repair_run_id: repairPreview.repair_run_id,
      });
      setRepairApplyResult(result);
      setRepairPreview(null);
      await loadStatus();
      onApplySuccess?.();
    } catch (err) {
      setRepairActionError(parseScheduleRepairError(err, 'Schedule repair apply failed.'));
    } finally {
      setApplyingRepair(false);
    }
  };

  return (
    <ScheduleSyncStatusPanelView
      leagueId={leagueId}
      status={status}
      runs={runs}
      loading={loading}
      error={error}
      syncing={syncing}
      applying={applying}
      syncPreview={syncPreview}
      syncApplyResult={syncApplyResult}
      syncError={syncError}
      repairStatus={repairStatus}
      repairStatusLoading={repairStatusLoading}
      repairStatusError={repairStatusError}
      previewingRepair={previewingRepair}
      applyingRepair={applyingRepair}
      repairPreview={repairPreview}
      repairApplyResult={repairApplyResult}
      repairActionError={repairActionError}
      onRefresh={() => {
        void loadStatus();
      }}
      onSync={() => {
        void handleSync();
      }}
      onApply={() => {
        void handleApply();
      }}
      onPreviewRepair={() => {
        void handlePreviewRepair();
      }}
      onApplyRepair={() => {
        void handleApplyRepair();
      }}
      detailsOnly={detailsOnly}
    />
  );
};

export default ScheduleSyncStatusPanel;
